import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { API_BASE_URL } from '../constants/system'
import { AutoCoderEntity, EntityMetricBounds, EntityQuery } from '../types/gotypes'
import { extractErrorMessage } from '../utils/apitools'
// The one renderer-side metric module in this codebase: the value shape the
// server sends and the percent-vs-number formatting rule. Reused rather than
// re-declared so the entity browser and the social dashboard cannot drift on
// what "0.368" means.
import { EntityMetricValue } from '../components/social-media/metrics-config'
import useEntityFilterSummary from './useEntityFilterSummary'

/**
 * The job-wide ENTITY browser's reader.
 *
 * An entity is the thing records are about — a video, a post — and it exists
 * independently of any pipeline, so the browser reads the job's own entity
 * routes (`POST /entities/query` for the page, `POST /entities/metrics/query`
 * for its numbers) rather than anything pipeline-scoped.
 *
 * Two requests per page, never one per row: the list, then ONE grouped metrics
 * query for exactly the entity ids that page holds. The metric catalog lives
 * server-side (`goapi/pkg/autocoder/entitymetrics/catalog.go`) and reaches the
 * browser through the filter summary's bounds, so the columns are the metrics
 * THIS job actually carries, in catalog order — never a hard-coded list.
 */

/**
 * The request/response wrappers of the two entity routes, hand-mirrored here
 * (rather than imported from `gotypes.ts`) for the same reason `useSocialMedia`
 * mirrors its own: they are `pkg/server` structs with no generator
 * registration, so the generator does not emit them. `EntityQuery` itself IS
 * generated and is imported.
 */
export interface EntityListRequest {
  entity?: EntityQuery
  limit?: number
  offset?: number
}

interface EntityListResponse {
  entities: AutoCoderEntity[]
  /** Matches BEFORE paging — the browser's "N entities" count. */
  totalCount: number
}

interface EntityMetricsGroup {
  /** With `groupBy: {kind: 'entity'}` this is the entity id. */
  key: string
  entityCount: number
  metrics: Record<string, EntityMetricValue>
}

interface EntityMetricsQueryResponse {
  groups: EntityMetricsGroup[]
  totalEntities: number
}

/**
 * How many of the job's metrics become table columns. The filter summary
 * returns them in catalog order, which puts the volume metrics an operator
 * scans a list by (reach, views, engagements, post comments) first and the
 * derived rates — which read as a summary, not as a column — after. The detail
 * view shows ALL of them, so nothing is hidden, only deferred.
 */
export const ENTITY_METRIC_COLUMN_COUNT = 4

/** The metrics that become columns: the job's own, catalog order, first N. */
export const entityMetricColumns = (bounds: EntityMetricBounds[]): EntityMetricBounds[] =>
  (bounds || []).slice(0, ENTITY_METRIC_COLUMN_COUNT)

/**
 * What to call an entity. `name` is the connector's title for it; a video that
 * arrived with none is still identifiable by the id the platform knows it by,
 * which is what the operator would paste into a URL.
 */
export const entityDisplayName = (entity: AutoCoderEntity): string =>
  entity?.name?.trim() || entity?.externalId || entity?.id || '—'

/**
 * Published dates are rendered as UTC days, matching every other timestamp in
 * the AutoCoder surfaces (see `utils/importBatches.ts`): a local-zone render
 * silently moves a video by a day for half the world.
 */
export const formatPublishedDate = (value: unknown): string => {
  if (typeof value !== 'string' || value.trim() === '') return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/**
 * A column the browser can sort by. `metric:<key>` sorts on a number that only
 * exists for the loaded page (metrics arrive per page, by design), which is why
 * the table says so out loud.
 */
export type EntitySortKey = 'name' | 'platform' | 'kind' | 'published' | `metric:${string}`
export type EntitySortDirection = 'asc' | 'desc'

export interface EntitySort {
  key: EntitySortKey
  direction: EntitySortDirection
}

/** The list's server order: newest published first, undated last. */
export const DEFAULT_ENTITY_SORT: EntitySort = { key: 'published', direction: 'desc' }

const sortValue = (
  entity: AutoCoderEntity,
  key: EntitySortKey,
  metricsByEntity: Record<string, Record<string, EntityMetricValue>>,
): string | number | undefined => {
  if (key.startsWith('metric:')) {
    return metricsByEntity[entity.id]?.[key.slice('metric:'.length)]?.value
  }
  switch (key) {
    case 'name':
      return entityDisplayName(entity).toLowerCase()
    case 'platform':
      return entity.platform?.trim().toLowerCase() || undefined
    case 'kind':
      return entity.kind?.trim().toLowerCase() || undefined
    case 'published': {
      if (typeof entity.publishedDate !== 'string') return undefined
      const t = Date.parse(entity.publishedDate)
      return Number.isNaN(t) ? undefined : t
    }
    default:
      return undefined
  }
}

/**
 * Sorts the loaded page. Missing values sort LAST in both directions: a video
 * with no reach figure is not the smallest reach, it is an unknown one, and
 * flipping the arrow must not parade the unknowns to the top.
 */
export const sortEntities = (
  entities: AutoCoderEntity[],
  sort: EntitySort,
  metricsByEntity: Record<string, Record<string, EntityMetricValue>>,
): AutoCoderEntity[] => {
  const factor = sort.direction === 'asc' ? 1 : -1
  return [...(entities || [])].sort((a, b) => {
    const av = sortValue(a, sort.key, metricsByEntity)
    const bv = sortValue(b, sort.key, metricsByEntity)
    if (av === undefined && bv === undefined) return 0
    if (av === undefined) return 1
    if (bv === undefined) return -1
    if (av === bv) return 0
    return (av < bv ? -1 : 1) * factor
  })
}

/** entity id → its metrics, from a `groupBy: entity` metrics response. */
export const metricsByEntityFrom = (
  groups: EntityMetricsGroup[] | undefined,
): Record<string, Record<string, EntityMetricValue>> => {
  const map: Record<string, Record<string, EntityMetricValue>> = {}
  ;(groups || []).forEach(group => {
    if (group?.key) map[group.key] = group.metrics || {}
  })
  return map
}

export interface UseEntityMetricsResult {
  metricsByEntity: Record<string, Record<string, EntityMetricValue>>
  loading: boolean
  error: string | null
}

/**
 * The numbers for a known set of entities, in ONE grouped request.
 *
 * Shared by the table (the page's rows) and the detail view (one entity, every
 * metric), so both read the server catalog's `displayName`/`format` rather than
 * re-deciding percent-vs-number in the browser.
 */
export const useEntityMetrics = (
  customer: string,
  job: string,
  entityIds: string[],
  metricKeys: string[],
  enabled = true,
): UseEntityMetricsResult => {
  // Sorted so that the same page reached by a different route is one cache
  // entry, not two.
  const idsKey = [...entityIds].sort().join(',')
  const metricsKey = [...metricKeys].sort().join(',')

  const query = useQuery<EntityMetricsQueryResponse, Error>({
    queryKey: ['autocoder', customer, job, 'entityMetrics', { idsKey, metricsKey }],
    queryFn: async () => {
      try {
        const res = await axios.post<EntityMetricsQueryResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/entities/metrics/query`,
          {
            entity: { entityIds },
            metrics: metricKeys.map(metric => ({ metric })),
            groupBy: { kind: 'entity' },
          },
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    // The endpoint rejects an empty metric list by name, so a job carrying no
    // metrics at all asks nothing rather than asking for a 400.
    enabled: enabled && !!customer && !!job && entityIds.length > 0 && metricKeys.length > 0,
    staleTime: 30 * 1000,
  })

  const metricsByEntity = useMemo(() => metricsByEntityFrom(query.data?.groups), [query.data])

  return {
    metricsByEntity,
    loading: query.isLoading,
    error: query.error ? extractErrorMessage(query.error) : null,
  }
}

export interface EntityBrowserFilters {
  /**
   * The receipt door: the entities one fetch DISCOVERED. `import_batch_id` is
   * first-wins, so a later tick finding new comments on a known video leaves it
   * filed under the fetch that first saw it — the copy says "discovered by"
   * for that reason.
   */
  importBatchId?: string
  /** Free text over entity name/url. */
  search?: string
  /** Only entities from these platforms (`youtube`, `tiktok`, …). */
  platforms?: string[]
}

/** The list request one page of the browser sends. */
export const entityListRequest = (
  filters: EntityBrowserFilters,
  page: number,
  rowsPerPage: number,
): EntityListRequest => {
  const entity: EntityQuery = {}
  if (filters.importBatchId) entity.importBatchId = filters.importBatchId
  if (filters.search) entity.search = filters.search
  if (filters.platforms?.length) entity.platforms = filters.platforms
  return { entity, limit: rowsPerPage, offset: page * rowsPerPage }
}

export interface UseEntityBrowserOptions {
  customer: string
  job: string
  filters: EntityBrowserFilters
  page: number
  rowsPerPage: number
  /** False while the containing tab is not showing. */
  enabled?: boolean
}

export interface UseEntityBrowserResult {
  entities: AutoCoderEntity[]
  total: number
  metricsByEntity: Record<string, Record<string, EntityMetricValue>>
  /** The metric columns to render (job's own metrics, catalog order). */
  metricColumns: EntityMetricBounds[]
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
}

/**
 * One page of the job's entities plus that page's metrics.
 *
 * No polling: entities appear when a fetch lands, and the receipts ledger is
 * the surface that watches fetches. Refresh is explicit here.
 */
export const useEntityBrowser = ({
  customer,
  job,
  filters,
  page,
  rowsPerPage,
  enabled = true,
}: UseEntityBrowserOptions): UseEntityBrowserResult => {
  const { importBatchId, search, platforms } = filters

  // Which metrics this job carries at all, with their display metadata. The
  // summary is deliberately unfiltered and cached, so the columns do not
  // change shape as the user filters — and the detail view shares the request.
  const summary = useEntityFilterSummary({ customer, job, enabled })

  const listQuery = useQuery<EntityListResponse, Error>({
    queryKey: [
      'autocoder',
      customer,
      job,
      'entities',
      { importBatchId, search, platforms, page, rowsPerPage },
    ],
    queryFn: async () => {
      const body = entityListRequest({ importBatchId, search, platforms }, page, rowsPerPage)
      try {
        const res = await axios.post<EntityListResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/entities/query`,
          body,
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    enabled: enabled && !!customer && !!job,
    staleTime: 30 * 1000,
  })

  const entities = useMemo(() => listQuery.data?.entities || [], [listQuery.data])
  const metricColumns = useMemo(() => entityMetricColumns(summary.metrics), [summary.metrics])
  const entityIds = useMemo(() => entities.map(e => e.id), [entities])
  const metricKeys = useMemo(() => metricColumns.map(m => m.metric), [metricColumns])

  const metrics = useEntityMetrics(customer, job, entityIds, metricKeys, enabled)

  return {
    entities,
    total: listQuery.data?.totalCount ?? 0,
    metricsByEntity: metrics.metricsByEntity,
    metricColumns,
    loading: listQuery.isLoading,
    isFetching: listQuery.isFetching || metrics.loading,
    // The list's failure is the one that empties the table; a metrics failure
    // only costs the numbers, and is reported the same way so neither is silent.
    error: listQuery.error ? extractErrorMessage(listQuery.error) : metrics.error,
    refetch: () => {
      void listQuery.refetch()
    },
  }
}

export default useEntityBrowser
